import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/api';
import { useToast } from '../../hooks/useToast';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  TablePagination,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  LocalOffer as OfferIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  TrendingUp as TrendingIcon,
  Refresh as RefreshIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { ConfirmModal } from '../../components/ui/Modal';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { Toast } from '../../components/ui/Toast';
import { Breadcrumbs } from '../../components/ui/Breadcrumbs';

interface PromoCode {
  _id: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  isActive: boolean;
  usageCount: number;
  maxUsage?: number;
  expiresAt?: string;
  createdAt: string;
  providerId?: string;
  partnerId?: string;
  partnerName?: string;
  partnerEmail?: string;
}

const PromoCodesList: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Toast notifications
  const { toasts, removeToast, success, error: showError } = useToast();

  // Pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    severity: 'info' | 'warning' | 'error' | 'success';
  }>({ open: false, title: '', message: '', onConfirm: () => {}, severity: 'warning' });

  // Filtrer les codes promo
  const filteredPromoCodes = promoCodes.filter(code => {
    const matchesSearch = code.code.toLowerCase().includes(search.toLowerCase()) ||
                         (code.partnerName && code.partnerName.toLowerCase().includes(search.toLowerCase())) ||
                         (code.partnerEmail && code.partnerEmail.toLowerCase().includes(search.toLowerCase()));
    const matchesActive = activeFilter === 'all' ||
                         (activeFilter === 'true' && code.isActive) ||
                         (activeFilter === 'false' && !code.isActive);
    const matchesType = typeFilter === 'all' || code.discountType === typeFilter;
    return matchesSearch && matchesActive && matchesType;
  });

  // Paginer les résultats
  const paginatedPromoCodes = filteredPromoCodes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    loadPromoCodes();
  }, []);

  const loadPromoCodes = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('@TikodAdmin:token');
      const response = await fetch(buildApiUrl('/api/promocodes'), {
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des codes promo');
      }

      const data = await response.json();
      setPromoCodes(data.promoCodes || data);
      success('Codes promo chargés avec succès');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirm = (promoCodeId: string, code: string) => {
    setConfirmModal({
      open: true,
      title: 'Supprimer le code promo',
      message: `Êtes-vous sûr de vouloir supprimer le code promo ${code} ? Cette action est irréversible.`,
      onConfirm: () => handleDelete(promoCodeId),
      severity: 'error'
    });
  };

  const handleDelete = async (promoCodeId: string) => {
    setConfirmModal({ ...confirmModal, open: false });
    try {
      const token = localStorage.getItem('@TikodAdmin:token');
      const response = await fetch(buildApiUrl(`/api/promocodes/${promoCodeId}`), {
        method: 'DELETE',
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression du code promo');
      }

      success('Code promo supprimé avec succès');
      loadPromoCodes();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la suppression';
      showError(errorMessage);
    }
  };

  const getDiscountDisplay = (type: string, value: number) => {
    return type === 'percentage' ? `${value}%` : `${value}€`;
  };

  const getStatusColor = (isActive: boolean, expiresAt?: string) => {
    if (!isActive) return 'error';
    if (expiresAt && new Date(expiresAt) < new Date()) return 'warning';
    return 'success';
  };

  const getStatusLabel = (isActive: boolean, expiresAt?: string) => {
    if (!isActive) return 'Inactif';
    if (expiresAt && new Date(expiresAt) < new Date()) return 'Expiré';
    return 'Actif';
  };

  // Handle pagination changes
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Composant carte code promo pour mobile
  const PromoCodeCard: React.FC<{ promoCode: PromoCode }> = ({ promoCode }) => (
    <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <OfferIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            {promoCode.code}
          </Typography>
          <Chip
            label={getStatusLabel(promoCode.isActive, promoCode.expiresAt)}
            size="small"
            color={getStatusColor(promoCode.isActive, promoCode.expiresAt) as any}
          />
        </Box>

        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Réduction:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {getDiscountDisplay(promoCode.discountType, promoCode.discountValue)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Type:
            </Typography>
            <Chip
              label={promoCode.discountType === 'percentage' ? 'Pourcentage' : 'Montant fixe'}
              size="small"
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />
              <Typography variant="body2" color="text.secondary">
                Utilisations:
              </Typography>
            </Box>
            <Typography variant="body2">
              {promoCode.usageCount}
              {promoCode.maxUsage && ` / ${promoCode.maxUsage}`}
            </Typography>
          </Box>

          {promoCode.partnerName && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />
                <Typography variant="body2" color="text.secondary">
                  Partenaire:
                </Typography>
              </Box>
              <Typography variant="body2">
                {promoCode.partnerName}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />
              <Typography variant="body2" color="text.secondary">
                Expire le:
              </Typography>
            </Box>
            <Typography variant="body2">
              {promoCode.expiresAt
                ? new Date(promoCode.expiresAt).toLocaleDateString('fr-FR')
                : 'Jamais'
              }
            </Typography>
          </Box>
        </Stack>
      </CardContent>

      <Divider />

      <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/promocodes/${promoCode._id}`)}
          color="primary"
        >
          Modifier
        </Button>

        <IconButton
          size="small"
          onClick={() => showDeleteConfirm(promoCode._id, promoCode.code)}
          color="error"
          title="Supprimer"
        >
          <DeleteIcon />
        </IconButton>
      </CardActions>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
        <Breadcrumbs items={[{ label: 'Codes promo', path: '/promocodes' }]} />
        <Paper sx={{ p: 3 }}>
          <TableSkeleton rows={5} />
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs items={[{ label: 'Codes promo', path: '/promocodes' }]} />

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}
        >
          Gestion des Codes Promo
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/promocodes/new')}
          fullWidth={isMobile}
          sx={{
            background: 'linear-gradient(135deg, #129baf 0%, #0e7989 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0e7989 0%, #096670 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 20px rgba(18, 155, 175, 0.4)',
            },
          }}
        >
          Nouveau code promo
        </Button>
      </Box>

      {/* Search & Filters */}
      <Paper sx={{ p: { xs: 2, sm: 2 }, mb: 3 }}>
        <Box sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          alignItems: 'center',
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <TextField
            label="Rechercher"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
          />

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={activeFilter}
              label="Statut"
              onChange={(e) => { setActiveFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="true">Actifs</MenuItem>
              <MenuItem value="false">Inactifs</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Tous les types</MenuItem>
              <MenuItem value="percentage">Pourcentage</MenuItem>
              <MenuItem value="fixed">Montant fixe</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            onClick={loadPromoCodes}
            color="primary"
            title="Actualiser"
            sx={{ ml: 'auto' }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filteredPromoCodes.length} code{filteredPromoCodes.length > 1 ? 's' : ''} promo trouvé{filteredPromoCodes.length > 1 ? 's' : ''}
        {search && ` pour "${search}"`}
      </Typography>

      {/* Vue mobile - Cartes */}
      {isMobile ? (
        <Box>
          {paginatedPromoCodes.map((promoCode) => (
            <PromoCodeCard key={promoCode._id} promoCode={promoCode} />
          ))}
          {paginatedPromoCodes.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {search || activeFilter !== 'all' || typeFilter !== 'all'
                  ? 'Aucun code promo ne correspond aux filtres'
                  : 'Aucun code promo trouvé'}
              </Typography>
            </Paper>
          )}
          {filteredPromoCodes.length > rowsPerPage && (
            <TablePagination
              component="div"
              count={filteredPromoCodes.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{ mt: 2 }}
            />
          )}
        </Box>
      ) : (
        // Vue desktop - Tableau
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Réduction</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Utilisations</TableCell>
                <TableCell>Partenaire</TableCell>
                <TableCell>Créé le</TableCell>
                <TableCell>Expire le</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPromoCodes.map((promoCode) => (
                <TableRow key={promoCode._id} hover>
                  <TableCell component="th" scope="row">
                    <strong>{promoCode.code}</strong>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={promoCode.discountType === 'percentage' ? 'Pourcentage' : 'Montant fixe'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <strong>{getDiscountDisplay(promoCode.discountType, promoCode.discountValue)}</strong>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(promoCode.isActive, promoCode.expiresAt)}
                      size="small"
                      color={getStatusColor(promoCode.isActive, promoCode.expiresAt) as any}
                    />
                  </TableCell>
                  <TableCell>
                    {promoCode.usageCount}
                    {promoCode.maxUsage && ` / ${promoCode.maxUsage}`}
                  </TableCell>
                  <TableCell>
                    {promoCode.partnerName || promoCode.partnerEmail || '-'}
                  </TableCell>
                  <TableCell>
                    {new Date(promoCode.createdAt).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell>
                    {promoCode.expiresAt
                      ? new Date(promoCode.expiresAt).toLocaleDateString('fr-FR')
                      : 'Jamais'
                    }
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/promocodes/${promoCode._id}`)}
                      color="primary"
                      title="Modifier"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => showDeleteConfirm(promoCode._id, promoCode.code)}
                      color="error"
                      title="Supprimer"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedPromoCodes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {search || activeFilter !== 'all' || typeFilter !== 'all'
                        ? 'Aucun code promo ne correspond aux filtres'
                        : 'Aucun code promo trouvé'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {filteredPromoCodes.length > rowsPerPage && (
            <TablePagination
              component="div"
              count={filteredPromoCodes.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          )}
        </TableContainer>
      )}

      {/* Toast notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          open={true}
          onClose={() => removeToast(toast.id)}
          message={toast.message}
          severity={toast.severity}
        />
      ))}

      {/* Confirm Modal */}
      <ConfirmModal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ ...confirmModal, open: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        severity={confirmModal.severity}
        confirmText="Confirmer"
        cancelText="Annuler"
      />
    </Container>
  );
};

export default PromoCodesList; 